import { useEffect, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { NAV } from '../data/nav.ts'
import ThemeBehaviors from './ThemeBehaviors.tsx'

const BASE_BODY =
  'wp-theme-x tribe-no-js x-renew x-full-width-layout-active x-navbar-fixed-top-active cornerstone-v7_9_4 x-v10_9_4'

const BODY_BY_PATH: Record<string, string> = {
  '/': `home wp-singular page-template page-template-template-blank-4 page page-id-967 ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/season-1/': `wp-singular page-template page-template-template-blank-4 page ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/season-2/': `wp-singular page-template page-template-template-blank-4 page ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/game/': `wp-singular page-template page-template-template-blank-4 page ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/spectrum-comic-book/': `wp-singular page-template page-template-template-blank-4 page ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/press/': `wp-singular page-template page-template-template-blank-4 page ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/faq/': `wp-singular page-template page-template-template-layout-content-sidebar page page-id-416 ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
  '/updates/': `page page-id-updates ${BASE_BODY} x-content-sidebar-active`,
  '/con-man-game-forums/': `forum-archive bbpress ${BASE_BODY} x-content-sidebar-active`,
  '/game/termsofuse/': `wp-singular page ${BASE_BODY} x-content-sidebar-active x-page-title-disabled`,
}

function bodyClassFor(pathname: string) {
  if (BODY_BY_PATH[pathname]) return BODY_BY_PATH[pathname]
  if (pathname.startsWith('/updates/page/')) return BODY_BY_PATH['/updates/']
  if (pathname.startsWith('/con-man-game-forums')) return BODY_BY_PATH['/con-man-game-forums/']
  return `wp-singular single single-post ${BASE_BODY} x-content-sidebar-active`
}

function Icon({
  className,
  solid,
  brand,
}: {
  className: string
  solid?: string
  brand?: string
}) {
  return (
    <i
      className={className}
      {...(brand ? { 'data-x-icon-b': brand } : { 'data-x-icon-s': solid })}
      aria-hidden="true"
    />
  )
}

function Social() {
  return (
    <div className="x-social-global">
      <a href="https://www.facebook.com/conmanseries" className="facebook" title="Facebook" target="_blank" rel="noopener noreferrer">
        <Icon className="x-framework-icon x-icon-facebook-square" brand={'\uf082'} />
        <span className="visually-hidden">Facebook</span>
      </a>
      <a href="https://twitter.com/conmanseries" className="x twitter" title="X" target="_blank" rel="noopener noreferrer">
        <Icon className="x-framework-icon x-icon-x-square" brand={'\ue61a'} />
        <span className="visually-hidden">X</span>
      </a>
      <a href="https://vimeo.com/ondemand/conman" className="vimeo" title="Vimeo" target="_blank" rel="noopener noreferrer">
        <Icon className="x-framework-icon x-icon-vimeo-square" brand={'\uf194'} />
        <span className="visually-hidden">Vimeo</span>
      </a>
      <a href="https://instagram.com/conmanthegame/" className="instagram" title="Instagram" target="_blank" rel="noopener noreferrer">
        <Icon className="x-framework-icon x-icon-instagram" brand={'\uf16d'} />
        <span className="visually-hidden">Instagram</span>
      </a>
      <a href="#" className="rss" title="RSS" target="_blank" rel="noopener noreferrer">
        <Icon className="x-framework-icon x-icon-rss-square" solid={'\uf143'} />
        <span className="visually-hidden">RSS</span>
      </a>
    </div>
  )
}

function MenuItems({ id, footer = false }: { id: string; footer?: boolean }) {
  const { pathname } = useLocation()
  const caret = (
    <i className="x-icon x-framework-icon x-framework-icon-menu" aria-hidden="true" data-x-icon-s={'\uf103'} />
  )
  return (
    <ul id={id} className="x-nav">
      {NAV.map((item, index) => {
        const active =
          item.to === '/'
            ? pathname === '/'
            : pathname === item.to || pathname.startsWith(item.to)
        return (
          <li
            key={item.to}
            id={footer ? undefined : `menu-item-${[412, 434, 1166, 413, 2891, 560, 423, 421][index]}`}
            className={`menu-item menu-item-type-post_type menu-item-object-page ${active ? 'current-menu-item current_page_item' : ''}`}
          >
            <NavLink to={item.to} end={item.to === '/'} aria-current={active ? 'page' : undefined}>
              {footer ? (
                item.label
              ) : (
                <span>
                  {item.label}
                  {caret}
                </span>
              )}
            </NavLink>
          </li>
        )
      })}
      {footer ? null : (
        <li
          className={`menu-item menu-item-has-children x-menu-item x-menu-item-bbpress${pathname.startsWith('/con-man-game-forums') ? ' current-menu-parent' : ''}`}
        >
          <NavLink to="/con-man-game-forums/" className="x-btn-navbar-bbpress">
            <span>
              <Icon className="x-framework-icon x-icon-comment" solid={'\uf075'} />
              <span className="visually-hidden">Forums</span>
              <span className="x-hidden-desktop" aria-hidden="true">
                {' '}
                Forums
              </span>
            </span>
          </NavLink>
          <ul className="sub-menu">
            <li className="menu-item menu-item-bbpress-navigation">
              <a href="/con-man-game-forums/search/" className="cf">
                <Icon className="x-framework-icon x-icon-search" solid={'\uf002'} /> <span>Forums Search</span>
              </a>
            </li>
            <li className="menu-item menu-item-bbpress-navigation">
              <a href="/masuk-bangg/" className="cf">
                <Icon className="x-framework-icon x-icon-sign-in" solid={'\uf2f6'} /> <span>Log in</span>
              </a>
            </li>
          </ul>
        </li>
      )}
    </ul>
  )
}

export default function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const year = new Date().getFullYear()

  useEffect(() => {
    document.body.className = bodyClassFor(pathname)
    document.documentElement.classList.remove('no-js')
    document.documentElement.classList.add('js')
  }, [pathname])

  return (
    <div id="x-root" className="x-root">
      <ThemeBehaviors />
      <div className="x-skip-links">
        <a className="x-skip-link" href="#x-main">
          Skip to content
        </a>
      </div>
      <div id="top" className="site">
        <header className="masthead masthead-inline" role="banner">
          <div className="x-topbar">
            <div className="x-topbar-inner x-container max width">
              <p className="p-info">
                An EMMY® nominated series from Alan Tudyk, Nathan Fillion, and PJ Haarsma. <b>And now it&apos;s a game too!</b>
              </p>
              <Social />
            </div>
          </div>
          <div className="x-navbar-wrap">
            <div className="x-navbar">
              <div className="x-navbar-inner">
                <div className="x-container max width">
                  <NavLink to="/" className="x-brand img">
                    <img src="/assets/images/2015/09/Header-Logo.webp" alt="Con Man" width={750} height={350} decoding="async" />
                  </NavLink>
                  <a
                    href="#"
                    id="x-btn-navbar"
                    className="x-btn-navbar collapsed"
                    data-x-toggle="collapse-b"
                    data-x-toggleable="x-nav-wrap-mobile"
                    aria-expanded="false"
                    aria-controls="x-nav-wrap-mobile"
                    role="button"
                  >
                    <Icon className="x-framework-icon x-icon-bars" solid={'\uf0c9'} />
                    <span className="visually-hidden">Navigation</span>
                  </a>
                  <nav className="x-nav-wrap desktop" role="navigation">
                    <MenuItems id="menu-main-menu" />
                  </nav>
                  <div
                    id="x-nav-wrap-mobile"
                    className="x-nav-wrap mobile x-collapsed"
                    data-x-toggleable="x-nav-wrap-mobile"
                    data-x-toggle-collapse="1"
                    aria-hidden="true"
                    aria-labelledby="x-btn-navbar"
                  >
                    <MenuItems id="menu-main-menu-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {children}

        <footer className="x-colophon bottom" role="contentinfo">
          <div className="x-container max width">
            <Social />
            <MenuItems id="menu-main-menu-2" footer />
            <div className="x-colophon-content">
              All images and content ©Con Man Productions LLC 2016 - {year}
            </div>
          </div>
        </footer>
      </div>
      <div
        id="x-widgetbar"
        className="x-widgetbar x-collapsed"
        data-x-toggleable="x-widgetbar"
        data-x-toggle-collapse="1"
        aria-hidden="true"
        aria-labelledby="x-btn-widgetbar"
      >
        <div className="x-widgetbar-inner">
          <div className="x-container max width">
            <div className="x-column x-md x-1-1 last" />
          </div>
        </div>
      </div>
      <a
        href="#"
        id="x-btn-widgetbar"
        className="x-btn-widgetbar collapsed"
        data-x-toggle="collapse-b"
        data-x-toggleable="x-widgetbar"
        aria-expanded="false"
        aria-controls="x-widgetbar"
        role="button"
      >
        <i className="x-framework-icon x-icon-plus-circle" data-x-icon-s={'\uf055'} aria-hidden="true" />
        <span className="visually-hidden">Toggle the Widgetbar</span>
      </a>
      <span className="x-scroll-top right fade" title="Back to Top" data-rvt-scroll-top role="button" tabIndex={0} aria-label="Back to Top">
        <Icon className="x-framework-icon x-icon-angle-up" solid={'\uf106'} />
      </span>
    </div>
  )
}
